import React, {useCallback, useEffect, useRef, useState} from "react";
import {Box, Flex, Text, Tooltip, keyframes, useColorModeValue} from "@chakra-ui/react";

// Soft "breathing" pulse around the status dot.
const pulse = keyframes`
    0%   { transform: scale(0.9); opacity: 0.7; }
    70%  { transform: scale(2.2); opacity: 0; }
    100% { transform: scale(0.9); opacity: 0; }
`;

// How often we actively re-verify real connectivity, and how long a probe may
// take before we treat the network as unreachable.
const PROBE_INTERVAL_MS = 20 * 1000;
const PROBE_TIMEOUT_MS = 8 * 1000;

// Same-origin asset used to actively verify connectivity. The app's own favicon
// is always served (HTTP 200), so probing it never produces the 404 noise a
// non-existent backend path would, while a real network drop still rejects.
const PROBE_URL = `${process.env.PUBLIC_URL || ""}/favicon.ico`;

const navigatorOnline = () =>
    typeof navigator !== "undefined" && typeof navigator.onLine === "boolean" ? navigator.onLine : true;

// navigator.onLine only tells us a network interface exists. A wifi with no
// internet still reports `true`, so we actively probe a same-origin asset with
// `cache: no-store`: it resolves (2xx) when we truly have connectivity and
// rejects only on a real network failure / timeout.
const probeReachable = async () => {
    if (!navigatorOnline()) {
        return false;
    }

    if (typeof fetch !== "function") {
        return true; // Can't probe; trust the interface flag.
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

    try {
        const response = await fetch(`${PROBE_URL}?_=${Date.now()}`, {
            cache: "no-store",
            signal: controller.signal,
        });
        return response.ok;
    } catch (e) {
        return false;
    } finally {
        clearTimeout(timer);
    }
};

const OnlineStatusIndicator = ({secondary}) => {
    const [isOnline, setIsOnline] = useState(navigatorOnline);
    const [checking, setChecking] = useState(false);
    const isMounted = useRef(true);

    const runCheck = useCallback(async () => {
        // Fast path: no interface at all => definitely offline.
        if (!navigatorOnline()) {
            if (isMounted.current) setIsOnline(false);
            return;
        }

        if (isMounted.current) setChecking(true);
        const reachable = await probeReachable();
        if (isMounted.current) {
            setIsOnline(reachable);
            setChecking(false);
        }
    }, []);

    useEffect(() => {
        isMounted.current = true;
        runCheck();

        const interval = setInterval(runCheck, PROBE_INTERVAL_MS);
        const handleOnline = () => runCheck();
        const handleOffline = () => isMounted.current && setIsOnline(false);
        const handleFocus = () => runCheck();
        const handleVisibility = () => {
            if (document.visibilityState === "visible") runCheck();
        };

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        window.addEventListener("focus", handleFocus);
        document.addEventListener("visibilitychange", handleVisibility);

        return () => {
            isMounted.current = false;
            clearInterval(interval);
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
            window.removeEventListener("focus", handleFocus);
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, [runCheck]);

    const defaultText = useColorModeValue("gray.600", "gray.200");
    const textColor = secondary ? "white" : defaultText;
    const defaultBorder = useColorModeValue("gray.200", "whiteAlpha.300");
    const borderColor = secondary ? "whiteAlpha.600" : defaultBorder;
    const hoverBg = useColorModeValue("gray.50", "whiteAlpha.200");

    const dotColor = isOnline ? "green.400" : "red.400";
    const label = isOnline ? "Online" : "Offline";
    const tooltip = isOnline
        ? "Connected — bells and announcements run normally"
        : "No internet — offline schedules keep running on this device";

    return (
        <Tooltip
            label={checking && !isOnline ? "Checking connection…" : tooltip}
            hasArrow
            placement="bottom"
            borderRadius="8px"
            fontSize="xs"
        >
            <Flex
                align="center"
                gap="6px"
                h="32px"
                px={{base: "8px", md: "10px"}}
                borderRadius="10px"
                border="1px solid"
                borderColor={borderColor}
                cursor="pointer"
                userSelect="none"
                transition="all 0.2s ease"
                _hover={{bg: hoverBg, borderColor: dotColor}}
                onClick={runCheck}
                aria-label={`Network status: ${label}`}
                role="status"
            >
                <Box position="relative" w="10px" h="10px">
                    {isOnline && (
                        <Box
                            position="absolute"
                            inset="0"
                            borderRadius="full"
                            bg={dotColor}
                            animation={`${pulse} 2s ease-out infinite`}
                        />
                    )}
                    <Box
                        position="absolute"
                        inset="0"
                        borderRadius="full"
                        bg={dotColor}
                        boxShadow={`0 0 6px var(--chakra-colors-${isOnline ? "green" : "red"}-400)`}
                    />
                </Box>
                <Text
                    display={{base: "none", md: "block"}}
                    fontSize="sm"
                    fontWeight="semibold"
                    color={textColor}
                >
                    {label}
                </Text>
            </Flex>
        </Tooltip>
    );
};

export default OnlineStatusIndicator;
